import { lazy, type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { BuildDetailPage } from '@/features/builds/routes';
import { ExecutionRunDetailPage } from '@/features/executionRuns/pages/ExecutionRunDetailPage';
import { SuiteExecutionDetailPage, TestExecutionDetailPage } from '@/features/executions/routes';
import { FeatureDetailPage, FeaturesPage } from '@/features/features/routes';
import { OnboardingPage } from '@/features/onboarding/pages/OnboardingPage';
import { OrganisationSettingsPage } from '@/features/organisations/pages/OrganisationSettingsPage';
import { PipelineDetailPage, PipelinesPage } from '@/features/pipelines/routes';
import { ProjectSettingsPage } from '@/features/projects/routes';
import { ReleaseDetailPage, ReleasesPage } from '@/features/releases/routes';
import { SuiteDetailPage, SuitesPage } from '@/features/suites/routes';
import { TestDetailPage, TestsPage } from '@/features/tests/routes';
import { ResultsFileDetailPage, UploadDetailPage } from '@/features/uploads/routes';
import {
  BuildsIcon,
  FeaturesIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  ReleasesIcon,
  SettingsIcon,
  SuitesIcon,
  TestsIcon,
} from '@/ui/primitives';
import { DocsPage } from '../../pages/DocsPage';
import { LandingPage } from '../../pages/LandingPage';
import { PlanPage } from '../../pages/PlanPage';
import { PrivacyRequestsPage } from '../../pages/PrivacyRequestsPage';
import { ReportsPage } from '../../pages/ReportsPage';
import { ResetPasswordConfirmPage } from '../../pages/ResetPasswordConfirmPage';
import { ResetPasswordPage } from '../../pages/ResetPasswordPage';
import { SessionExpired } from '../../pages/SessionExpired';
import { SuperuserPage } from '../../pages/SuperuserPage';
import { TermlyPolicyEmbed } from '../../pages/TermlyPolicyEmbed';
import { UserSettingsPage } from '../../pages/UserSettingsPage';
import { VerifyEmailPage } from '../../pages/VerifyEmailPage';
import { AUTH_ROUTE_PATHS } from './authRoutePaths';
import type { AuthError, AuthSession } from '@/auth/types';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

const UploadsPage = lazy(async () => {
  const module = await import('@/features/uploads/pages/ExecutionRunsPage');
  return { default: module.UploadsPage };
});
const SignInPage = lazy(async () => {
  const module = await import('../../pages/SignInPage');
  return { default: module.SignInPage };
});
const SignUpPage = lazy(async () => {
  const module = await import('../../pages/SignUpPage');
  return { default: module.SignUpPage };
});
const AuthCallbackPage = lazy(async () => {
  const module = await import('../../pages/AuthCallbackPage');
  return { default: module.AuthCallbackPage };
});
const authBypassClerkEnabled = import.meta.env.VITE_AUTH_BYPASS_CLERK === 'true';

export type AppRouteContext = {
  session: AuthSession | null;
  authLoading: boolean;
  authError: AuthError | null;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signUp: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    acceptedPolicies: boolean;
  }) => Promise<void>;
  signOut: () => void;
  refreshSession: () => Promise<boolean>;
  setSessionDirect: (next: AuthSession | null) => void;
  isAdmin: boolean;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  projects: ProjectDto[];
  onAddProject: () => void;
};

export type AppRouteDefinition = {
  key: string;
  path: string;
  title?: string;
  description?: string;
  featureFlag?: string;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  requiresSuperuser?: boolean;
  devOnly?: boolean;
  element: (context: AppRouteContext) => ReactElement;
  nav?: {
    label: string;
    icon: ReactElement;
    requiresAdmin?: boolean;
    requiresSuperuser?: boolean;
    secondary?: string;
    secondaryPath?: string;
    disabled?: boolean;
    secondaryDisabled?: boolean;
  };
};

const createPolicyPage =
  (dataId: string): AppRouteDefinition['element'] =>
  () => <TermlyPolicyEmbed dataId={dataId} />;

export const appRouteDefinitions: AppRouteDefinition[] = [
  { key: 'landing', path: '/', title: '[ F L O W Q A ]', element: () => <LandingPage /> },
  { key: 'docs', path: '/docs', title: 'Docs', element: () => <DocsPage /> },
  {
    key: 'cookie-policy',
    path: '/cookie-policy',
    title: 'Cookie Policy',
    element: createPolicyPage('07370c92-4ecc-41fe-836e-4487b26a9aa8'),
  },
  {
    key: 'cookie-policy-html',
    path: '/cookie-policy.html',
    title: 'Cookie Policy',
    element: () => <Navigate to="/cookie-policy" replace />,
  },
  {
    key: 'privacy-requests',
    path: '/privacy-requests',
    title: 'Privacy Requests',
    element: () => <PrivacyRequestsPage />,
  },
  {
    key: 'privacy-policy',
    path: '/privacy-policy',
    title: 'Privacy Policy',
    element: createPolicyPage('9ec55e0f-2e88-41a9-803b-5e1180513847'),
  },
  {
    key: 'privacy-policy-html',
    path: '/privacy-policy.html',
    title: 'Privacy Policy',
    element: () => <Navigate to="/privacy-policy" replace />,
  },
  {
    key: 'terms-of-service',
    path: '/terms-of-service',
    title: 'Terms of Service',
    element: createPolicyPage('6698af93-e6c9-4ffa-8f24-0ff34b3e022d'),
  },
  {
    key: 'acceptable-use-policy',
    path: '/acceptable-use-policy',
    title: 'Acceptable Use Policy',
    element: createPolicyPage('cea0d21a-5c0b-4a92-bd65-7501d1af6e98'),
  },
  {
    key: 'terms-of-service-html',
    path: '/terms-of-service.html',
    title: 'Terms of Service',
    element: () => <Navigate to="/terms-of-service" replace />,
  },
  {
    key: 'sign-in',
    path: `${AUTH_ROUTE_PATHS.signIn}/*`,
    title: 'Sign In',
    element: ({ session, authError, signOut }) => (
      <SignInPage session={session} authError={authError} signOut={signOut} />
    ),
  },
  {
    key: 'sign-up',
    path: `${AUTH_ROUTE_PATHS.signUp}/*`,
    title: 'Sign Up',
    element: ({ session, authLoading, authError, signOut }) => (
      <SignUpPage
        session={session}
        authLoading={authLoading}
        authError={authError}
        signOut={signOut}
      />
    ),
  },
  {
    key: 'auth-callback',
    path: AUTH_ROUTE_PATHS.authCallback,
    title: 'Auth Callback',
    element: () =>
      authBypassClerkEnabled ? <Navigate to="/sign-in" replace /> : <AuthCallbackPage />,
  },
  {
    key: 'reset-password',
    path: AUTH_ROUTE_PATHS.resetPassword,
    title: 'Reset Password',
    element: ({ session }) => <ResetPasswordPage session={session} />,
  },
  {
    key: 'reset-password-confirm',
    path: AUTH_ROUTE_PATHS.resetPasswordConfirm,
    title: 'Choose New Password',
    element: ({ session }) => <ResetPasswordConfirmPage session={session} />,
  },
  {
    key: 'verify-email',
    path: AUTH_ROUTE_PATHS.verifyEmail,
    title: 'Verify Email',
    element: ({ session }) => <VerifyEmailPage session={session} />,
  },
  {
    key: 'onboarding',
    path: '/onboarding',
    title: 'Get Started',
    requiresAuth: true,
    element: ({ session, organisations }) => (
      <OnboardingPage session={session} organisations={organisations} />
    ),
  },
  {
    key: 'dashboard',
    path: '/dashboard',
    title: 'Dashboard',
    requiresAuth: true,
    featureFlag: 'dashboard',
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'reports',
    path: '/reports',
    title: 'Reports',
    requiresAuth: true,
    featureFlag: 'reports',
    element: () => <ReportsPage />,
  },
  {
    key: 'settings',
    path: '/account-settings',
    title: 'Account Settings',
    requiresAuth: true,
    element: ({ session }) => <UserSettingsPage session={session} />,
  },
  {
    key: 'settings-redirect',
    path: '/settings',
    title: 'Account Settings',
    requiresAuth: true,
    element: () => <Navigate to="/account-settings" replace />,
  },
  {
    key: 'plan',
    path: '/plan-settings',
    title: 'Plan Settings',
    requiresAuth: true,
    element: () => <PlanPage />,
  },
  {
    key: 'superuser',
    path: '/superuser',
    title: 'Superuser',
    requiresAuth: true,
    requiresSuperuser: true,
    element: ({ session }) => <SuperuserPage session={session} />,
    nav: { label: 'Superuser', icon: <SettingsIcon fontSize="small" />, requiresSuperuser: true },
  },
  {
    key: 'feature-flags-redirect',
    path: '/feature-flags',
    title: 'Superuser',
    requiresAuth: true,
    requiresSuperuser: true,
    element: () => <Navigate to="/superuser" replace />,
  },
  {
    key: 'projects',
    path: '/projects',
    title: 'Projects',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'releases',
    path: '/releases',
    title: 'Releases',
    requiresAuth: true,
    featureFlag: 'releases',
    element: () => <ReleasesPage />,
    nav: { label: 'Releases', icon: <ReleasesIcon fontSize="small" /> },
  },
  {
    key: 'features',
    path: '/features',
    title: 'Features',
    requiresAuth: true,
    featureFlag: 'features',
    element: () => <FeaturesPage />,
    nav: { label: 'Features', icon: <FeaturesIcon fontSize="small" /> },
  },
  {
    key: 'organisations',
    path: '/organisations/:slug',
    title: 'Organisation',
    requiresAuth: true,
    featureFlag: 'organisations',
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'organisation-settings',
    path: '/organisation-settings',
    title: 'Organisation Settings',
    requiresAuth: true,
    featureFlag: 'organisations',
    element: ({ session }) => <OrganisationSettingsPage session={session} />,
  },
  {
    key: 'project-detail',
    path: '/projects/:slug',
    title: 'Project',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'project-settings',
    path: '/project-settings',
    title: 'Project Settings',
    requiresAuth: true,
    element: ({ session }) => <ProjectSettingsPage session={session} />,
  },
  {
    key: 'uploads',
    path: '/execution-runs',
    title: 'Execution Runs',
    description:
      'View execution runs, pass rates, status trends, and upload outcomes across your project.',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: () => <UploadsPage />,
    nav: {
      label: 'Execution Runs',
      icon: <PlayCircleFilledWhiteOutlinedIcon fontSize="small" />,
    },
  },
  {
    key: 'uploads-redirect',
    path: '/uploads',
    title: 'Execution Runs',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'upload-detail',
    path: '/uploads/:id',
    title: 'Upload',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: ({ session }) => <UploadDetailPage session={session} />,
  },
  {
    key: 'execution-run-detail',
    path: '/execution-runs/:id',
    title: 'Execution Run',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: ({ session }) => <ExecutionRunDetailPage session={session} />,
  },
  {
    key: 'suites',
    path: '/suites',
    title: 'Suites',
    requiresAuth: true,
    element: () => <SuitesPage />,
    nav: {
      label: 'Suites',
      icon: <SuitesIcon fontSize="small" />,
    },
  },
  {
    key: 'tests',
    path: '/tests',
    title: 'Tests',
    requiresAuth: true,
    element: () => <TestsPage />,
    nav: {
      label: 'Tests',
      icon: <TestsIcon fontSize="small" />,
    },
  },
  {
    key: 'pipelines',
    path: '/pipelines',
    title: 'Pipelines',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: () => <PipelinesPage />,
    nav: {
      label: 'Pipelines',
      icon: <BuildsIcon fontSize="small" />,
    },
  },
  {
    key: 'pipeline-detail',
    path: '/pipelines/:pipelineSlug',
    title: 'Pipeline',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <PipelineDetailPage session={session} />,
  },
  {
    key: 'build-detail',
    path: '/pipelines/:pipelineSlug/runs/:runNumber',
    title: 'Build',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <BuildDetailPage session={session} />,
  },
  {
    key: 'results-files-redirect',
    path: '/results-files',
    title: 'Results Files',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: () => <Navigate to="/execution-runs#results-files" replace />,
  },
  {
    key: 'results-file-detail',
    path: '/results-files/:slug',
    title: 'Results Files',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: ({ session }) => <ResultsFileDetailPage session={session} />,
  },
  {
    key: 'suite-execution-detail',
    path: '/suites/:suiteSlug/runs/:runNumber',
    title: 'Suite Execution',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: ({ session }) => <SuiteExecutionDetailPage session={session} />,
  },
  {
    key: 'test-execution-detail',
    path: '/tests/:testSlug/runs/:runNumber',
    title: 'Test Execution',
    requiresAuth: true,
    featureFlag: 'uploads',
    element: ({ session }) => <TestExecutionDetailPage session={session} />,
  },
  {
    key: 'feature-detail',
    path: '/features/:slug',
    title: 'Feature',
    requiresAuth: true,
    featureFlag: 'features',
    element: ({ session }) => <FeatureDetailPage session={session} />,
  },
  {
    key: 'release-detail',
    path: '/releases/:slug',
    title: 'Release',
    requiresAuth: true,
    featureFlag: 'releases',
    element: ({ session }) => <ReleaseDetailPage session={session} />,
  },
  {
    key: 'suite-detail',
    path: '/suites/:slug',
    title: 'Suite',
    requiresAuth: true,
    element: ({ session }) => <SuiteDetailPage session={session} />,
  },
  {
    key: 'test-detail',
    path: '/tests/:slug',
    title: 'Test',
    requiresAuth: true,
    element: ({ session }) => <TestDetailPage session={session} />,
  },
  {
    key: 'session-expired',
    path: '/session-expired',
    title: 'Session Expired',
    element: () => <SessionExpired />,
  },
];
